import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { PlanningRoute } from 'src/app/core/interfaces/planning-route';
import { RouteItinerary } from 'src/app/core/interfaces/route-itinerary';
import { RouteReferenceAuthorizedPoint } from 'src/app/core/interfaces/route-reference-authorized-point';
import { DateManager } from 'src/app/core/managers/date.manager';
import { ServiceMessages } from 'src/app/core/messages/service-messages.enum';
import { Global } from 'src/app/core/resources/global';
import { Permission } from 'src/app/core/resources/permission';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { Utils } from 'src/app/core/resources/utils';
import { PlanningRouteService } from 'src/app/core/services/planning-route.service';
import { SnackBarService } from 'src/app/core/services/snackBar.service';
import { OptionsAutocomplete } from 'src/app/core/interfaces/optionsAutocomplete';
import { StandardMapComponent } from 'src/app/shared/standard-map/standard-map.component';

@Component({
  selector: 'app-cargo-planning-route',
  templateUrl: './cargo-planning-route.component.html',
  styleUrls: ['./cargo-planning-route.component.scss']
})
export class CargoPlanningRouteComponent implements OnInit {
  @ViewChild(StandardMapComponent) standardMap: StandardMapComponent;
  selectedItinerary: string = '';
  permission = Permission;
  cargo: Cargo;
  routePlan: PlanningRoute;
  showRouteMap: boolean = false;
  mapOptions: OptionsAutocomplete = {
    zoom: 6
  };
  itineraries: RouteItinerary[] = [];
  cargoNearnessIndexes: { [key: string]: { address: string, index: number }[] } = {};
  private planningMapReady = false;
  private pendingCriticZonesItinerary: RouteItinerary;

  constructor(
    private spinner: NgxSpinnerService,
    private snackBarService: SnackBarService,
    private global: Global,
    private utils: Utils,
    private router: Router,
    private permissionRole: PermissionRole,
    private planningRouteService: PlanningRouteService,
    public dialogRef: MatDialogRef<CargoPlanningRouteComponent>,
    @Inject(MAT_DIALOG_DATA) public paramsDialog: {
      mode: "edit" | "detail",
      itineraryId: string,
      routePlanId?: string,
      cargo?: Cargo,
      itineraryOverviewPolylines?: string
    },
  ) {
    if (this.paramsDialog.itineraryId) this.selectedItinerary = this.paramsDialog.itineraryId;
    if (this.paramsDialog.cargo) this.cargo = this.paramsDialog.cargo;
  }

  ngOnInit() {
    this.setRouteMapOptions();
    if (this.paramsDialog.routePlanId && this.paramsDialog.mode === 'edit')
      this.getRouteById();
    else if (this.paramsDialog.itineraryId && this.paramsDialog.mode === 'detail')
      this.getItinerary(this.selectedItinerary);
    else {
      this.snackBarService.openSnackBar("Ocurrió un error al acceder al plan de ruta", undefined, 'error');
      this.dialogRef.close();
    }
  }

  private setRouteMapOptions() {
    if (!this.paramsDialog || this.paramsDialog.mode !== 'detail')
      return;
    const overviewPolyline = this.paramsDialog.itineraryOverviewPolylines
      ? this.paramsDialog.itineraryOverviewPolylines
      : (this.paramsDialog.cargo && this.paramsDialog.cargo.itineraryOverviewPolylines ? this.paramsDialog.cargo.itineraryOverviewPolylines : null);
    if (!overviewPolyline || !overviewPolyline.trim()) {
      this.showRouteMap = false;
      return;
    }
    this.showRouteMap = true;
    this.mapOptions = {
      ...this.mapOptions,
      initialEncodedPolyline: true,
      encodedPolylineAsDirections: true,
      encodedPolylineWaypointsLimit: 20,
      encodedPolyline: overviewPolyline,
      encodedPolylineColor: '#584796'
    };
  }

  private getRouteById() {
    this.spinner.show();
    this.planningRouteService.getRoute(this.paramsDialog.routePlanId).subscribe(
      (response: PlanningRoute) => {
        if (!response || !response.id || !response.itineraries || !response.itineraries.length || !response.active) {
          this.snackBarService.openSnackBar("Ocurrió un error al acceder al plan de ruta", undefined, 'error');
          this.spinner.hide();
          this.dialogRef.close();
          return;
        }
        this.routePlan = response;
        this.routePlan.itineraries.forEach(async (itinerary) => {
          await this.getItinerary(itinerary.id, itinerary);
        })
        this.spinner.hide();
      }, (error) => {
        this.snackBarService.openSnackBar("Ocurrió un error al acceder al plan de ruta", undefined, 'error');
        this.spinner.hide();
        this.dialogRef.close();
      }
    )
  }

  private async getItinerary(id: string, incompleteItinerary?: RouteItinerary) {
    try {
      const itinerary = await this.planningRouteService.getItinerary(id ? id : incompleteItinerary.id).toPromise();
      if (itinerary && itinerary.id) {
        this.itineraries.push(itinerary);
        if (this.cargo) {
          const indexes = this.planningRouteService.getCargoNearnessIndexes(this.cargo, itinerary);
          this.cargoNearnessIndexes[itinerary.id] = indexes;
        }
        this.renderCriticZonesOnMap(itinerary);
      }
      else if (incompleteItinerary) {
        this.itineraries.push(incompleteItinerary);
        if (this.cargo) {
          const indexes = this.planningRouteService.getCargoNearnessIndexes(this.cargo, incompleteItinerary);
          this.cargoNearnessIndexes[incompleteItinerary.id] = indexes;
        }
      }
    } catch (e) {
      if (incompleteItinerary) {
        this.itineraries.push(incompleteItinerary);
        if (this.cargo) {
          const indexes = this.planningRouteService.getCargoNearnessIndexes(this.cargo, incompleteItinerary);
          this.cargoNearnessIndexes[incompleteItinerary.id] = indexes;
        }
      }
    }
  }

  onSelectItinerary(itinerary: RouteItinerary, $event: MouseEvent) {
    $event.stopPropagation();
    const validation = this.isValidItinerary(itinerary);
    if (!validation.isValid) {
      return this.snackBarService.openSnackBar(validation.message, undefined, 'alert');
    }

    const nearnessIndexes = this.getNearnessIndexes(itinerary.id);
    if (!nearnessIndexes || !nearnessIndexes.length) {
      return this.snackBarService.openSnackBar("No hay índices de cercanía para este itinerario", undefined, 'error');
    }
    this.selectedItinerary = itinerary.id;
  }

  public getDurationLabel(seconds: number): string {
    if (!seconds) return '';
    const durationMsg = [];
    const duration = DateManager.durationFormat(seconds, "seconds");
    if (duration.years > 0) durationMsg.push(`${duration.years} ${duration.years > 1 ? 'años' : 'año'}`);
    if (duration.months > 0) durationMsg.push(`${duration.months} ${duration.months > 1 ? 'meses' : 'mes'}`);
    if (duration.days > 0) durationMsg.push(`${duration.days} d`);
    if (duration.hours > 0) durationMsg.push(`${duration.hours} h`);
    if (duration.minutes > 0) durationMsg.push(`${duration.minutes} min`);

    return durationMsg.join(' ');
  }

  async saveItinerary() {
    if (!this.selectedItinerary) return this.snackBarService.openSnackBar("No ha seleccionado un itinerario", undefined, 'alert');
    if (this.selectedItinerary === this.paramsDialog.itineraryId) return this.dialogRef.close();
    const itinerarySelected = this.itineraries.find(itinerary => itinerary && itinerary.id === this.selectedItinerary);
    let cargoOverviewPolyline: { overviewPolyline: string, distance: number, duration: number, error?: string };
    try { cargoOverviewPolyline = await this.planningRouteService.getCargoOverviewPolyline(this.paramsDialog.cargo, itinerarySelected); } catch (e) { };
    if (cargoOverviewPolyline && cargoOverviewPolyline.error) return this.snackBarService.openSnackBar(cargoOverviewPolyline.error, undefined, 'error');
    const body = {
      cargoId: this.paramsDialog.cargo.id,
      routePlanId: this.paramsDialog.routePlanId,
      itineraryId: this.selectedItinerary,
      itineraryOverviewPolylines: cargoOverviewPolyline ? cargoOverviewPolyline.overviewPolyline : itinerarySelected.overviewPolylines
    }
    this.spinner.show();
    this.planningRouteService.setCargoRouteAndItinerary([body]).subscribe(
      (response) => {
        this.spinner.hide();
        this.snackBarService.openSnackBar("Se actualizó correctamente el itinerario");
        this.dialogRef.close({ state: true });
      }, (error) => {
        this.spinner.hide();
        this.snackBarService.openSnackBar(ServiceMessages.GENERAL_HTTP_ERROR, undefined, 'error');
      }
    )

  }

  goToCreateItinerary() {
    this.router.navigate(['/routes/planning/form/', this.paramsDialog.routePlanId]);
    this.dialogRef.close();
  }

  get isSomeItinerarySelectable(): boolean {
    const hasUnrestrictedPermission = this.permissionRole.hasPermission(
      this.permission.routes.module,
      this.permission.routes.assignRouteItineraryUnrestricted
    );

    return this.itineraries && this.itineraries.length &&
      this.itineraries.some(itinerary => {
        if (itinerary.active && hasUnrestrictedPermission) {
          return true;
        }
        return itinerary.active && this.isNearnessIndexValid(itinerary.id);
      });
  }

  get sortedItineraries(): RouteItinerary[] {
    return this.itineraries.sort((a, b) => a.name.localeCompare(b.name));
  }

  get hasCreateItineraryPermission(): boolean {
    return this.permissionRole.hasPermission(this.permission.routes.module, this.permission.routes.createRouteItinerary);
  }

  get hasUnrestrictedPermission(): boolean {
    return this.permissionRole.hasPermission(this.permission.routes.module, this.permission.routes.assignRouteItineraryUnrestricted);
  }

  getNearnessIndexes(itineraryId: string): { address: string, index: number }[] {
    return this.cargoNearnessIndexes[itineraryId];
  }

  isNearnessIndexValid(itineraryId: string): boolean {
    const indexes = this.cargoNearnessIndexes[itineraryId];
    return indexes && indexes.length && indexes.every(index => index && index.index >= 0.7);
  }
  isValidItinerary(itinerary: RouteItinerary): { isValid: boolean, message?: string } {
    if (!itinerary.active) return { isValid: false, message: 'El itinerario no está activo' };

    const permissionUnrestricted = this.permissionRole.hasPermission(
      this.permission.routes.module,
      this.permission.routes.assignRouteItineraryUnrestricted
    );
    if (!permissionUnrestricted && !this.isNearnessIndexValid(itinerary.id))
      return { isValid: false, message: 'No es posible asignar este itinerario, la dirección no supera el umbral de cercanía requerido (0.7)' };
    return { isValid: true };
  }
  getAuthorizedStopIcon(stop: RouteReferenceAuthorizedPoint): string {
    if (stop && stop.details && stop.details.pointType && stop.details.pointType.id) {
      const point = this.global.authorizedPoints.find(point => point.value === `point${stop.details.pointType.id}`);
      return point ? point.icon : null;
    }
    return null;
  }

  onPlanningMapReady() {
    this.planningMapReady = true;
    if (this.pendingCriticZonesItinerary)
      this.renderCriticZonesOnMap(this.pendingCriticZonesItinerary);
  }

  private renderCriticZonesOnMap(itinerary: RouteItinerary) {
    if (!this.paramsDialog || this.paramsDialog.mode !== 'detail') return;
    this.pendingCriticZonesItinerary = itinerary;
    if (!this.planningMapReady || !this.standardMap) return;
    this.standardMap.renderCriticZones(
      itinerary && itinerary.criticZones ? itinerary.criticZones : [],
      'planning-route'
    );
  }

}
